import { useEffect, useState } from "react";

/* Single shared source of truth for the PWA install prompt, so the global banner
   and any contextual nudges never fight over the one-shot beforeinstallprompt event. */
let deferredPrompt = null;
let installed = false;
const listeners = new Set();

function notify() {
  listeners.forEach((l) => l());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferredPrompt = null;
    notify();
  });
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const ios = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
  const safari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return ios && safari;
}

export async function promptInstall() {
  if (!deferredPrompt) return "unavailable";
  deferredPrompt.prompt();
  let outcome = "dismissed";
  try {
    const res = await deferredPrompt.userChoice;
    outcome = res?.outcome || "dismissed";
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("PWA install prompt failed", e);
  }
  deferredPrompt = null;
  notify();
  return outcome;
}

export function usePwaInstall() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);
  const standalone = isStandalone();
  return {
    installable: !!deferredPrompt && !installed && !standalone,
    iosSafari: isIOSSafari() && !standalone,
    standalone,
    installed,
    promptInstall,
  };
}
